import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button as AntButton, message, Modal, Space, Spin, Tooltip } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  ExternalLink,
  MapPin,
  Gauge,
  Bike,
  CheckCircle,
  Camera,
  ShieldCheck,
} from "lucide-react";
import type { ColumnsType } from "antd/es/table";

export interface ListingPhotoItem {
  id?: string;
  assetId?: string;
  url: string;
  width?: number | null;
  height?: number | null;
  position?: number;
  isCover?: boolean;
}

// Extended listing type returned by fetchListing (owner payload)
interface OwnerListing extends Omit<Listing, "location"> {
  photos?: ListingPhotoItem[];
  spec?: {
    engineCc?: number;
    motorPowerKw?: number;
    odometerKm?: number;
    powertrain?: string;
    transmission?: string;
    fuelSystem?: string;
    color?: string;
  };
  brand?: { name?: string; slug?: string };
  model?: { name?: string; slug?: string; modelYear?: number; manufacturingYear?: number };
  location?: {
    countryId?: string;
    cityId?: string | null;
    areaText?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
}

import {
  fetchSellerListings,
  fetchListing,
  fetchCities,
  deleteListing,
  listingPublish,
  listingPause,
  listingResume,
  listingSold,
  listingRelist,
  type Listing,
  type City,
} from "../api/auth";
import type { SellerListing } from "../api/types";
import {
  DataTable,
  Empty,
  ErrorNote,
  PageHeader,
  PageShell,
  Panel,
  StatusTag,
} from "../components/Page";

export default function AllBikesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{
    rows: SellerListing[];
    total: number;
    page: number;
    totalPages: number;
  } | null>(null);
  const [isPending, setIsPending] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Cities for friendly location labels
  const [cities, setCities] = useState<City[]>([]);

  // Details Modal State
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState<OwnerListing | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  const fetchList = useCallback(async (p: number) => {
    try {
      const result = await fetchSellerListings(p, 20);
      setData(result);
      setError(null);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? "Could not load your bikes.");
    } finally {
      setIsPending(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchList(page);
    })();
  }, [page, fetchList]);

  useEffect(() => {
    fetchCities().then(setCities).catch(() => {});
  }, []);

  const runAction = async (fn: () => Promise<unknown>, label: string) => {
    try {
      await fn();
      message.success(`Bike ${label}.`);
      await fetchList(page);
      if (selectedListing) {
        const refreshed = await fetchListing(selectedListing.id);
        setSelectedListing(refreshed as OwnerListing);
      }
    } catch (err: unknown) {
      message.error((err as { message?: string })?.message ?? `Could not ${label}.`);
    }
  };

  const handleOpenDetails = async (id: string) => {
    setDetailsModalOpen(true);
    setDetailsLoading(true);
    setActivePhotoIdx(0);
    try {
      const listing = await fetchListing(id);
      setSelectedListing(listing as OwnerListing);
    } catch (err: unknown) {
      message.error((err as { message?: string })?.message ?? "Could not load bike details.");
      setDetailsModalOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const confirmDelete = (id: string) => {
    Modal.confirm({
      title: "Delete this bike?",
      content: "The listing and its photos will be removed. This can't be undone.",
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeleting(id);
        try {
          await deleteListing(id);
          message.success("Bike deleted.");
          if (selectedListing?.id === id) {
            setDetailsModalOpen(false);
          }
          await fetchList(page);
        } catch (err: unknown) {
          message.error((err as { message?: string })?.message ?? "Could not delete.");
        } finally {
          setDeleting(null);
        }
      },
    });
  };

  if (error)
    return (
      <PageShell>
        <ErrorNote error={error} />
      </PageShell>
    );

  const rows = data?.rows ?? [];

  const columns: ColumnsType<SellerListing> = [
    {
      title: "Bike",
      dataIndex: "title",
      render: (_text, record) => (
        <div className="min-w-0">
          <div className="font-semibold text-ink flex items-center gap-2">
            <span
              onClick={() => handleOpenDetails(record.id)}
              className="cursor-pointer hover:text-primary hover:underline transition-colors line-clamp-1"
            >
              {record.title}
            </span>
          </div>
          <div className="flex items-center gap-2 font-mono text-[11.5px] text-ink-faint mt-0.5">
            <span>{record.publicId}</span>
            {record.price && (
              <span className="font-sans font-bold text-stone-800">
                • ৳{Number(record.price).toLocaleString()} BDT
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (_text, record) => <StatusTag status={record.status as string} />,
    },
    {
      title: "Listed",
      dataIndex: "createdAt",
      width: 120,
      render: (_text, record) => (
        <span className="tnum text-ink-muted text-xs">
          {new Date(record.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: "Actions",
      width: 380,
      render: (_text, record) => {
        const status = record.status as string;

        return (
          <Space size="small">
            <AntButton
              size="small"
              type="primary"
              ghost
              onClick={() => handleOpenDetails(record.id)}
              icon={<EyeOutlined />}
              className="border-primary text-primary hover:border-primary/80 font-medium"
            >
              View Details
            </AntButton>

            <AntButton
              size="small"
              onClick={() => navigate(`/vehicles/edit/${record.id}`)}
              icon={<EditOutlined />}
            >
              Edit
            </AntButton>

            {status === "PUBLISHED" ? (
              <AntButton
                size="small"
                danger
                onClick={() => runAction(() => listingPause(record.id), "paused")}
              >
                Pause
              </AntButton>
            ) : status === "PAUSED" ? (
              <AntButton
                size="small"
                type="primary"
                onClick={() => runAction(() => listingResume(record.id), "resumed")}
              >
                Resume
              </AntButton>
            ) : status === "SOLD" || status === "EXPIRED" ? (
              <AntButton
                size="small"
                onClick={() => runAction(() => listingRelist(record.id), "relisted")}
              >
                Relist
              </AntButton>
            ) : status === "REMOVED" ? null : (
              <AntButton
                size="small"
                type="primary"
                onClick={() => runAction(() => listingPublish(record.id), "published")}
              >
                Publish
              </AntButton>
            )}

            {status !== "SOLD" && status !== "REMOVED" && (
              <AntButton
                size="small"
                danger
                onClick={() => runAction(() => listingSold(record.id), "marked sold")}
              >
                Mark sold
              </AntButton>
            )}

            <Tooltip title="Delete bike">
              <AntButton
                size="small"
                danger
                disabled={deleting === record.id}
                onClick={() => confirmDelete(record.id)}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  const cityName = selectedListing?.location?.cityId
    ? cities.find((c) => c.id === selectedListing.location?.cityId)?.name ?? "Dhaka"
    : selectedListing?.cityId
      ? cities.find((c) => c.id === selectedListing.cityId)?.name ?? "Dhaka"
      : "Bangladesh";

  const photos = selectedListing?.photos ?? [];
  const activePhoto = photos[activePhotoIdx] ?? photos[0];
  const spec = selectedListing?.spec;

  const publicMarketplaceUrl = selectedListing?.publicId
    ? `https://bikerhat.com/listings/${selectedListing.publicId}`
    : null;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Inventory"
        title="All Bikes"
        description="Manage your motorcycle listings, view details, update prices, and track performance."
        actions={
          <AntButton type="primary" icon={<PlusOutlined />} onClick={() => navigate("/vehicles/add")}>
            Add Bike
          </AntButton>
        }
      />

      <Panel flush>
        {isPending ? (
          <div className="flex justify-center py-16">
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <Empty title="No vehicles yet">
            Add your first vehicle to start selling on BikerHat.
          </Empty>
        ) : (
          <DataTable<SellerListing>
            columns={columns}
            rows={rows}
            total={data?.total}
            page={page}
            pageSize={20}
            onPageChange={setPage}
          />
        )}
      </Panel>

      {/* ── Rich Bike Details Modal ────────────────────────────────────────── */}
      <Modal
        open={detailsModalOpen}
        onCancel={() => setDetailsModalOpen(false)}
        footer={
          <div className="flex items-center justify-between w-full px-1">
            <div className="text-xs text-ink-muted">
              {selectedListing && (
                <span>
                  Listed: {new Date(selectedListing.createdAt).toLocaleDateString()} • Ref:{" "}
                  <span className="font-mono">{selectedListing.publicId}</span>
                </span>
              )}
            </div>
            <Space>
              <AntButton onClick={() => setDetailsModalOpen(false)}>Close</AntButton>
              {selectedListing && (
                <AntButton
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setDetailsModalOpen(false);
                    navigate(`/vehicles/edit/${selectedListing.id}`);
                  }}
                >
                  Edit Bike
                </AntButton>
              )}
            </Space>
          </div>
        }
        width={760}
        destroyOnClose
        centered
      >
        {detailsLoading || !selectedListing ? (
          <div className="py-16 flex flex-col items-center justify-center gap-3">
            <Spin size="large" />
            <p className="text-xs text-ink-muted">Loading motorcycle details…</p>
          </div>
        ) : (
          <div className="space-y-5 pt-2">
            {/* Header info */}
            <div className="border-b border-stone-200 pb-4">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <StatusTag status={selectedListing.status} />
                    <span className="font-mono text-xs px-2 py-0.5 rounded bg-stone-100 text-stone-600 border border-stone-200">
                      {selectedListing.publicId}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-semibold uppercase tracking-wider text-[10px]">
                      {selectedListing.condition}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold tracking-tight text-ink">
                    {selectedListing.title}
                  </h2>
                  <p className="text-xs text-ink-muted mt-0.5 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-stone-400" />
                    <span>
                      {cityName}
                      {selectedListing.location?.areaText || selectedListing.areaText
                        ? ` • ${selectedListing.location?.areaText || selectedListing.areaText}`
                        : ""}
                    </span>
                  </p>
                </div>

                <div className="sm:text-right">
                  <div className="text-2xl font-black tracking-tight text-stone-900 font-mono">
                    ৳{Number(
                      typeof selectedListing.price === "number"
                        ? selectedListing.price
                        : (selectedListing.price as any)?.amount ?? 0,
                    ).toLocaleString()}{" "}
                    <span className="text-xs font-normal text-stone-500 font-sans">BDT</span>
                  </div>
                  <div className="text-xs text-stone-500">
                    {selectedListing.negotiable ? "Price Negotiable" : "Fixed Price"}
                  </div>
                  {publicMarketplaceUrl && selectedListing.status === "PUBLISHED" && (
                    <a
                      href={publicMarketplaceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary font-semibold hover:underline mt-1.5"
                    >
                      <span>View Live on BikerHat</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Photo Gallery */}
            <div>
              {photos.length > 0 ? (
                <div className="space-y-2">
                  <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-stone-100 border border-stone-200">
                    <img
                      src={activePhoto?.url}
                      alt={selectedListing.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2.5 right-2.5 bg-black/70 text-white text-[11px] px-2.5 py-1 rounded-md font-medium backdrop-blur-xs flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5" />
                      <span>
                        Photo {activePhotoIdx + 1} of {photos.length}
                      </span>
                    </div>
                    {activePhoto?.isCover && (
                      <span className="absolute top-2.5 left-2.5 bg-primary text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shadow-xs">
                        Cover Photo
                      </span>
                    )}
                  </div>

                  {photos.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {photos.map((p: ListingPhotoItem, idx: number) => (
                        <button
                          key={p.url || idx}
                          type="button"
                          onClick={() => setActivePhotoIdx(idx)}
                          className={`relative h-16 w-24 shrink-0 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                            activePhotoIdx === idx
                              ? "border-primary ring-2 ring-primary/30"
                              : "border-stone-200 hover:border-stone-300 opacity-75 hover:opacity-100"
                          }`}
                        >
                          <img
                            src={p.url}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="aspect-video w-full rounded-xl bg-stone-50 border border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 gap-1.5">
                  <Bike className="w-10 h-10 opacity-30" />
                  <span className="text-xs">No photos uploaded for this bike</span>
                </div>
              )}
            </div>

            {/* Key Specifications Grid */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2.5 flex items-center gap-1.5">
                <Gauge className="w-3.5 h-3.5 text-stone-400" />
                <span>Motorcycle Technical Specifications</span>
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
                <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                  <span className="text-[11px] text-stone-500 block">Brand & Model</span>
                  <span className="font-semibold text-stone-900 block truncate mt-0.5">
                    {selectedListing.brand?.name ?? "—"} {selectedListing.model?.name ?? ""}
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                  <span className="text-[11px] text-stone-500 block">Year of Mfg / Model</span>
                  <span className="font-semibold text-stone-900 block mt-0.5">
                    {selectedListing.modelYear ?? selectedListing.manufacturingYear ?? "—"}
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                  <span className="text-[11px] text-stone-500 block">Engine / Power</span>
                  <span className="font-semibold text-stone-900 block mt-0.5">
                    {spec?.engineCc ? `${spec.engineCc} cc` : spec?.motorPowerKw ? `${spec.motorPowerKw} kW` : "—"}
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                  <span className="text-[11px] text-stone-500 block">Odometer Reading</span>
                  <span className="font-semibold text-stone-900 block mt-0.5 font-mono">
                    {spec?.odometerKm != null ? `${Number(spec.odometerKm).toLocaleString()} km` : "—"}
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                  <span className="text-[11px] text-stone-500 block">Powertrain</span>
                  <span className="font-semibold text-stone-900 block mt-0.5">
                    {spec?.powertrain ?? "PETROL"}
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                  <span className="text-[11px] text-stone-500 block">Transmission</span>
                  <span className="font-semibold text-stone-900 block mt-0.5">
                    {spec?.transmission ?? "MANUAL"}
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                  <span className="text-[11px] text-stone-500 block">Fuel System</span>
                  <span className="font-semibold text-stone-900 block mt-0.5">
                    {spec?.fuelSystem ?? "EFI"}
                  </span>
                </div>

                <div className="p-2.5 rounded-lg bg-stone-50 border border-stone-200">
                  <span className="text-[11px] text-stone-500 block">Color</span>
                  <span className="font-semibold text-stone-900 block mt-0.5">
                    {spec?.color || "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            {selectedListing.description && (
              <div className="border-t border-stone-200 pt-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">
                  Vehicle Story & Description
                </h3>
                <div className="text-xs text-stone-700 bg-stone-50 p-3 rounded-lg border border-stone-200 whitespace-pre-line leading-relaxed font-sans">
                  {selectedListing.description}
                </div>
              </div>
            )}

            {/* Dealership & Contact details */}
            <div className="border-t border-stone-200 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="flex items-center gap-2 text-stone-700">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>
                  Buyer Contact Preference: <strong className="text-stone-900">Live Chat & Phone Calls</strong>
                </span>
              </div>
              <div className="flex items-center gap-2 text-stone-700">
                <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                <span>
                  Listing Type: <strong className="text-stone-900">Verified Showroom Inventory</strong>
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </PageShell>
  );
}
